import React, { useMemo, useState } from 'react'
import { ArrowsRightLeftIcon, ChevronDownIcon, ChevronRightIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import type { EstoqueCategoria, EstoqueItem, EstoqueItemType, EstoqueLocal, EstoqueSaldo } from '../../types/estoque'
import EstoquePagination from './EstoquePagination'
import {
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  SELECT_CLASS,
  formatQuantity,
  itemTypeLabel,
  paginateRows,
  sortRows,
} from './estoqueUi'

type Props = {
  itens: EstoqueItem[]
  saldos: EstoqueSaldo[]
  locais: EstoqueLocal[]
  categorias: EstoqueCategoria[]
  loading: boolean
  onMovimentar: (itemId?: string, localId?: string) => void
  canUpdate: boolean
}

type LocationQty = {
  locationId: string
  locationName: string
  quantity: number
}

type ProductStockRow = {
  item: EstoqueItem
  total: number
  minStock: number
  belowMinimum: boolean
  locations: LocationQty[]
}

const EstoqueSaldosTab: React.FC<Props> = ({
  itens,
  saldos,
  locais,
  categorias,
  loading,
  onMovimentar,
  canUpdate,
}) => {
  const [search, setSearch] = useState('')
  const [localId, setLocalId] = useState('')
  const [itemType, setItemType] = useState<EstoqueItemType | ''>('')
  const [categoriaId, setCategoriaId] = useState('')
  const [sortKey, setSortKey] = useState('item')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const rows = useMemo(() => {
    const qtyByItem = new Map<string, Map<string, { name: string; quantity: number }>>()
    const itemById = new Map<string, EstoqueItem>()

    for (const item of itens) {
      itemById.set(item.id, item)
    }

    for (const saldo of saldos) {
      if (saldo.item && !itemById.has(saldo.item_id)) {
        itemById.set(saldo.item_id, saldo.item)
      }
      if (!qtyByItem.has(saldo.item_id)) qtyByItem.set(saldo.item_id, new Map())
      const locMap = qtyByItem.get(saldo.item_id)!
      const current = locMap.get(saldo.location_id)
      locMap.set(saldo.location_id, {
        name: saldo.location?.name || current?.name || '—',
        quantity: (current?.quantity || 0) + Number(saldo.quantity || 0),
      })
    }

    const term = search.trim().toLowerCase()
    const products: ProductStockRow[] = []

    for (const item of itemById.values()) {
      if (item.deleted_at) continue
      if (itemType && item.item_type !== itemType) continue
      if (categoriaId && item.category_id !== categoriaId) continue
      if (term) {
        const name = item.name.toLowerCase()
        const code = String(item.code ?? '').toLowerCase()
        if (!name.includes(term) && !code.includes(term)) continue
      }

      const locMap = qtyByItem.get(item.id) ?? new Map<string, { name: string; quantity: number }>()
      if (localId && !locMap.has(localId)) continue

      const locations: LocationQty[] = []
      const seen = new Set<string>()
      const orderedLocais = [...locais].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      for (const local of orderedLocais) {
        const recorded = locMap.get(local.id)
        if (!local.is_active && !recorded) continue
        if (!local.is_active && recorded && recorded.quantity === 0) continue
        seen.add(local.id)
        locations.push({
          locationId: local.id,
          locationName: local.name,
          quantity: recorded?.quantity ?? 0,
        })
      }
      for (const [id, entry] of locMap) {
        if (seen.has(id)) continue
        locations.push({ locationId: id, locationName: entry.name, quantity: entry.quantity })
      }

      let total = 0
      for (const entry of locMap.values()) total += entry.quantity
      const minStock = Number(item.min_stock || 0)

      products.push({
        item,
        total,
        minStock,
        belowMinimum: minStock > 0 && total < minStock,
        locations,
      })
    }

    return products
  }, [itens, saldos, locais, search, localId, itemType, categoriaId])

  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortOrder, (row, key) => {
        if (key === 'item') return row.item.name
        if (key === 'type') return row.item.item_type
        if (key === 'categoria') return row.item.category?.name
        if (key === 'total') return row.total
        if (key === 'minimo') return row.minStock
        return ''
      }),
    [rows, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page)
  const hasActiveFilters = Boolean(search.trim() || localId || itemType || categoriaId)
  const clearFilters = () => {
    setSearch('')
    setLocalId('')
    setItemType('')
    setCategoriaId('')
    setPage(1)
  }
  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }
  const toggleExpanded = (itemId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Saldos
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            O mínimo soma todos os locais. Clique no produto para ver o saldo de cada um.
          </Text>
        </div>
        {canUpdate && (
          <PermissionGuard moduleName={MODULE_NAME} permissionType="update" loadingComponent={PERM_LOADING} fallback={null}>
            <Button variant="solid" onClick={() => onMovimentar()} className="flex items-center gap-1">
              <ArrowsRightLeftIcon className="h-5 w-5" />
              Movimentar
            </Button>
          </PermissionGuard>
        )}
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Buscar</label>
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Buscar por nome"
            className="min-w-[180px]"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Local</label>
          <select
            value={localId}
            onChange={(e) => {
              setLocalId(e.target.value)
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todos</option>
            {locais.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</label>
          <select
            value={itemType}
            onChange={(e) => {
              setItemType(e.target.value as EstoqueItemType | '')
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todos</option>
            <option value="material">Material</option>
            <option value="brinde">Brinde</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Categoria</label>
          <select
            value={categoriaId}
            onChange={(e) => {
              setCategoriaId(e.target.value)
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todas</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center gap-1 whitespace-nowrap"
          >
            <XCircleIcon className="h-4 w-4" />
            Limpar filtros
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <SortableTableHeader sortKey="item" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Produto
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="type" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Tipo
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="categoria" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Categoria
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="total" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3 text-right">
                    Total
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="minimo" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3 text-right">
                    Mínimo
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum produto encontrado.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => {
                    const open = expandedIds.has(row.item.id)
                    return (
                      <React.Fragment key={row.item.id}>
                        <tr
                          className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/20 ${
                            row.belowMinimum ? 'bg-amber-50/70 dark:bg-amber-900/10' : ''
                          }`}
                          onClick={() => toggleExpanded(row.item.id)}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              toggleExpanded(row.item.id)
                            }
                          }}
                          tabIndex={0}
                          aria-expanded={open}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            <span className="flex items-start gap-2">
                              {open ? (
                                <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                              )}
                              <span>
                                {row.item.name}
                                {row.belowMinimum && (
                                  <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                    Abaixo do mínimo
                                  </span>
                                )}
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {itemTypeLabel(row.item.item_type)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {row.item.category?.name ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-white">
                            {formatQuantity(row.total)} {row.item.unit}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                            {formatQuantity(row.minStock)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {canUpdate && (
                              <PermissionGuard
                                moduleName={MODULE_NAME}
                                permissionType="update"
                                loadingComponent={PERM_LOADING}
                                fallback={null}
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    event.stopPropagation()
                                    onMovimentar(row.item.id)
                                  }}
                                >
                                  Movimentar
                                </Button>
                              </PermissionGuard>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50 dark:bg-gray-900/30">
                            <td colSpan={6} className="px-4 py-4">
                              <div className="ml-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                                <div className="mb-3 flex flex-wrap gap-6 text-sm">
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400">Estoque total</span>
                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                      {formatQuantity(row.total)} {row.item.unit}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400">Mínimo</span>
                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                      {formatQuantity(row.minStock)} {row.item.unit}
                                    </span>
                                  </div>
                                </div>
                                {row.locations.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma localidade cadastrada.</p>
                                ) : (
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                          Localidade
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                          Quantidade
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                          Ações
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                      {row.locations.map((location) => (
                                        <tr
                                          key={location.locationId}
                                          className={localId === location.locationId ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''}
                                        >
                                          <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                                            {location.locationName}
                                          </td>
                                          <td className="px-3 py-2 text-right text-sm font-medium text-gray-900 dark:text-white">
                                            {formatQuantity(location.quantity)} {row.item.unit}
                                          </td>
                                          <td className="px-3 py-2 text-right">
                                            {canUpdate && (
                                              <PermissionGuard
                                                moduleName={MODULE_NAME}
                                                permissionType="update"
                                                loadingComponent={PERM_LOADING}
                                                fallback={null}
                                              >
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => onMovimentar(row.item.id, location.locationId)}
                                                >
                                                  Movimentar
                                                </Button>
                                              </PermissionGuard>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <EstoquePagination page={page} total={sorted.length} onPageChange={setPage} pageSize={PAGE_SIZE} />
    </div>
  )
}

export default EstoqueSaldosTab
